import React, { useState, useEffect } from "react";
import "./Project.css";
import { isBefore } from "../../utils/dateUtils";
import { useFirestore } from "../../hooks/useFirestore";
import { useNavigate } from "react-router-dom";
import ProjectUpdateForm from "./ProjectUpdateForm";
import StackableAssignment from "./StackableAssignment";
import { collection, query, where, onSnapshot, getFirestore } from "firebase/firestore";

const db = getFirestore();

const ProjectSummary = ({ project }) => {
    const navigate = useNavigate();
    const today = new Date();
    const { deleteDocument } = useFirestore("projects");
    const [isEditing, setIsEditing] = useState(false);
    const [showStackModal, setShowStackModal] = useState(false);
    const [stackedAssignments, setStackedAssignments] = useState([]);

    const completed = project.completed;

    // Fetch stacked assignments
    useEffect(() => {
        const q = query(
            collection(db, "projects"),
            where("parentProjectId", "==", project.id)
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const stacked = [];
            snapshot.docs.forEach((doc) => {
                stacked.push({ id: doc.id, ...doc.data() });
            });
            setStackedAssignments(stacked);
        });

        return () => unsubscribe();
    }, [project.id]);

    const handleDelete = async () => {
        await deleteDocument(project.id);
        navigate("/");
    };

    const handleStackSuccess = () => {
        setShowStackModal(false);
        // Optionally show a success message
    };

    if (isEditing) {
        return (
            <div>
                <ProjectUpdateForm
                    docTitle={project.title}
                    docCompleted={project.completed}
                    docDueDate={project.dueDate}
                    docAssignmentDetails={project.details}
                    docAssignedCategory={project.category}
                    projectId={project.id}
                    docCompletedDate={project.completedDate}
                    docGrade={project.grade}
                    onCancel={() => setIsEditing(false)}
                />
            </div>
        );
    }

    return (
        <div className="project-summary">
            <h2 className="page-title">{project.title}</h2>
            {!completed && (
                <p
                    className={`due-date ${isBefore(project.dueDate.toDate(), today) && "late"}`}
                >
                    Assignment is due{" "}
                    {project.dueDate.toDate().toDateString()}
                </p>
            )}
            <p className="details">{project.details}</p>
            {project.completed && (
                <p>
                    Completed:{" "}
                    {project.completedDate.toDate().toDateString()}
                </p>
            )}
            {project.grade && <p>Grade: {project.grade}%</p>}
            
            {/* Show stacked assignments */}
            {stackedAssignments.length > 0 && (
                <div className="stacked-assignments">
                    <h3>Stacked Assignments ({stackedAssignments.length})</h3>
                    <ul>
                        {stackedAssignments.map((stacked) => (
                            <li key={stacked.id}>
                                <a href={`/projects/${stacked.id}`}>
                                    {stacked.title} - Due: {stacked.dueDate.toDate().toDateString()}
                                    {stacked.completed && " ✓"}
                                </a>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            <div className="comments-date">
                <p>
                    Created: {project.createdAt.toDate().toDateString()}
                </p>
            </div>
            
            <div className="button-container">
                <button className="btn btn-delete" onClick={handleDelete}>
                    Delete
                </button>
                <button
                    className="btn btn-edit"
                    onClick={() => setIsEditing(true)}
                >
                    Edit
                </button>                
                <button
                    className="btn btn-stack"
                    onClick={() => setShowStackModal(true)}
                >
                    Stack Assignment
                </button>
            </div>

            {/* Stack Assignment Modal */}
            {showStackModal && (
                <StackableAssignment
                    parentProject={project}
                    onClose={() => setShowStackModal(false)}
                    onSuccess={handleStackSuccess}
                />
            )}
        </div>
    );
};

export default ProjectSummary;
